import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Stack, Alert, InputAdornment, Collapse,
  Divider, Badge,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import QuizIcon from '@mui/icons-material/Quiz';
import { helpService, HelpFaq } from '../common/services/helpService';
import { Facilitator } from './facilitator';

const emptyForm = (): Omit<HelpFaq, 'faqId'> => ({
  question: '',
  answer: '',
  keywords: '',
  displayOrder: 0,
});

const CHIP_COLORS = [
  '#e3f2fd', '#fce4ec', '#e8f5e9', '#fff8e1',
  '#f3e5f5', '#e0f7fa', '#fff3e0', '#ede7f6',
];

const keywordChip = (k: string, idx: number) => (
  <Chip
    key={k}
    label={k}
    size="small"
    sx={{
      mr: 0.5, mb: 0.5,
      bgcolor: CHIP_COLORS[idx % CHIP_COLORS.length],
      fontWeight: 500,
      fontSize: 11,
      border: 'none',
    }}
  />
);

const ExpandableRow: React.FC<{
  faq: HelpFaq;
  idx: number;
  onEdit: (f: HelpFaq) => void;
  onDelete: (f: HelpFaq) => void;
}> = ({ faq, idx, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const keywords = faq.keywords.split(',').map(k => k.trim()).filter(Boolean);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 0 : undefined } }}
        onClick={() => setOpen(o => !o)}
      >
        <TableCell sx={{ color: 'text.secondary', fontSize: 12, width: 42 }}>{idx + 1}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" sx={{ p: 0.25 }}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
            <Typography variant="body2" fontWeight={600}>{faq.question}</Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {keywords.map((k, i) => keywordChip(k, i))}
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip label={faq.displayOrder} size="small" variant="outlined" sx={{ minWidth: 32, fontWeight: 600 }} />
        </TableCell>
        <TableCell align="center" onClick={e => e.stopPropagation()}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(faq)} color="primary">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDelete(faq)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, bgcolor: 'grey.50' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, px: 2, borderLeft: '3px solid', borderColor: 'primary.light' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Answer
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {faq.answer}
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const AdminFaqs: React.FC = () => {
  const [faqs, setFaqs] = useState<HelpFaq[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<HelpFaq | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<HelpFaq | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setFaqs(await helpService.getFaqs());
    } catch {
      setLoadError('Failed to load FAQs. Please check the API is running and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return faqs;
    return faqs.filter(f =>
      f.question.toLowerCase().includes(q) ||
      f.answer.toLowerCase().includes(q) ||
      f.keywords.toLowerCase().includes(q)
    );
  }, [faqs, search]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (faq: HelpFaq) => {
    setEditing(faq);
    setForm({ question: faq.question, answer: faq.answer, keywords: faq.keywords, displayOrder: faq.displayOrder });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.question.trim()) { setFormError('Question is required.'); return; }
    if (!form.answer.trim()) { setFormError('Answer is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editing) {
        const updated = await helpService.updateFaq(editing.faqId, form);
        setFaqs(prev => prev.map(f => f.faqId === editing.faqId ? updated : f));
      } else {
        const created = await helpService.createFaq(form);
        setFaqs(prev => [...prev, created]);
      }
      setDialogOpen(false);
    } catch {
      setFormError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await helpService.deleteFaq(deleteTarget.faqId);
      setFaqs(prev => prev.filter(f => f.faqId !== deleteTarget.faqId));
      setDeleteTarget(null);
    } catch {
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Facilitator>
    <Box sx={{ p: 3 }}>

      {/* Header */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
          <Box sx={{ bgcolor: 'primary.main', borderRadius: 2, p: 1, display: 'flex' }}>
            <QuizIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Help FAQs</Typography>
            <Typography variant="caption" color="text.secondary">
              {faqs.length} {faqs.length === 1 ? 'entry' : 'entries'} · shown to school users in the Help chat
            </Typography>
          </Box>
        </Box>
        <TextField
          size="small"
          placeholder="Search question, answer or keyword…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ whiteSpace: 'nowrap' }}>
          Add FAQ
        </Button>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" pt={8}><CircularProgress /></Box>
      ) : loadError ? (
        <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>{loadError}</Alert>
      ) : (
        <>
          {search && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
            </Typography>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ width: 42, fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Keywords</TableCell>
                  <TableCell sx={{ width: 80, textAlign: 'center', fontWeight: 700 }}>Order</TableCell>
                  <TableCell sx={{ width: 96, textAlign: 'center', fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      {search ? `No FAQs match "${search}".` : 'No FAQs yet. Click "Add FAQ" to create the first one.'}
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((faq, idx) => (
                  <ExpandableRow
                    key={faq.faqId}
                    faq={faq}
                    idx={idx}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700}>{editing ? 'Edit FAQ' : 'Add FAQ'}</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <TextField
              label="Question *"
              fullWidth
              value={form.question}
              onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
              size="small"
              placeholder="e.g. How do I download the Hall Ticket template?"
            />
            <TextField
              label="Answer *"
              fullWidth
              multiline
              minRows={4}
              value={form.answer}
              onChange={e => setForm(f => ({ ...f, answer: e.target.value }))}
              size="small"
              placeholder="Provide a clear, step-by-step answer for the school user…"
            />
            <TextField
              label="Keywords (comma-separated)"
              fullWidth
              value={form.keywords}
              onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))}
              size="small"
              helperText="Used by the bot to auto-match queries. E.g. hall ticket, download, template"
            />
            <TextField
              label="Display Order"
              type="number"
              value={form.displayOrder}
              onChange={e => setForm(f => ({ ...f, displayOrder: Number(e.target.value) }))}
              size="small"
              sx={{ width: 180 }}
              helperText="Lower numbers appear first in the Help chat"
            />
            {formError && (
              <Alert severity="error" sx={{ py: 0.5 }}>{formError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : undefined}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add FAQ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Delete FAQ?</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This FAQ will be permanently removed and will no longer appear in the Help chat.
          </Typography>
          {deleteTarget && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" fontWeight={600}>"{deleteTarget.question}"</Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Facilitator>
  );
};
